/**
 * ===============================================================================
 * 🚀 MIGRACIÓN: UtilidadesBuscadores.java → HttpNomenclaturaRepository (Angular)
 * ===============================================================================
 *
 * Reemplaza: UtilidadesBuscadores.llenarListBox() y métodos relacionados
 *
 * Ubicación: infrastructure/adapters/nomenclatura/ (arquitectura hexagonal)
 *
 * Endpoints Backend:
 * - GET /api/nomenclatura/buscar?parametro1=&parametro2=
 * - GET /api/nomenclatura/tipos-via
 * - GET /api/nomenclatura/apendices
 * - GET /api/nomenclatura/cardinalidades
 * - GET /api/nomenclatura/estratos
 * - GET /api/nomenclatura/tipos-direccion/{cdPersona}
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  BusquedaNomenclaturaRequest,
  BusquedaNomenclaturaResponse,
  NomenclaturaDto,
  TipoViaDto,
  ApendiceDto,
  CardinalidadDto,
  TipoDireccionDto
} from '../../../domain/shared/models/nomenclatura';
import { NomenclaturaRepository } from '../../../domain/service/nomenclatura/nomenclatura-repository';

@Injectable({ providedIn: 'root' })
export class HttpNomenclaturaRepository implements NomenclaturaRepository {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/nomenclatura`;

  // =========================================================================
  // DATOS LOCALES (Fallback) - Estratos no vienen de BD
  // =========================================================================

  private readonly ESTRATOS_LOCAL: NomenclaturaDto[] = [
    { codigo: '', descripcion: '' },
    { codigo: '1', descripcion: 'ESTRATO 1' },
    { codigo: '2', descripcion: 'ESTRATO 2' },
    { codigo: '3', descripcion: 'ESTRATO 3' },
    { codigo: '4', descripcion: 'ESTRATO 4' },
    { codigo: '5', descripcion: 'ESTRATO 5' },
    { codigo: '6', descripcion: 'ESTRATO 6' }
  ];

  // =========================================================================
  // MÉTODOS HTTP - Consultan al backend
  // =========================================================================

  /**
   * Búsqueda genérica de nomenclatura por categoría
   * Equivale a: UtilidadesBuscadores.llenarListBox()
   *
   * @param parametro2 Categoría (TIPO VIA, APENDICE, CARDINALIDAD)
   * @returns Lista de items de nomenclatura
   */
  async buscarPorCategoria(parametro2: string): Promise<NomenclaturaDto[]> {
    const params = new HttpParams()
      .set('parametro1', 'nomenclatura')
      .set('parametro2', parametro2);

    const response = await firstValueFrom(
      this.http.get<NomenclaturaDto[]>(`${this.baseUrl}/buscar`, { params })
    );

    return response || [];
  }

  /**
   * Lista tipos de vía desde BD
   * Equivale a: llenarListBox(..., "TIPO VIA", ...)
   */
  async listarTiposVia(): Promise<TipoViaDto[]> {
    return this.buscarPorCategoria('TIPO VIA') as Promise<TipoViaDto[]>;
  }

  /**
   * Lista apéndices desde BD
   * Equivale a: llenarListBox(..., "APENDICE", ...)
   */
  async listarApendices(): Promise<ApendiceDto[]> {
    // Agregar opción vacía al inicio
    const items = await this.buscarPorCategoria('APENDICE');
    return [{ codigo: '', descripcion: '' }, ...items] as ApendiceDto[];
  }

  /**
   * Lista cardinalidades desde BD
   * Equivale a: llenarListBox(..., "CARDINALIDAD", ...)
   */
  async listarCardinalidades(): Promise<CardinalidadDto[]> {
    // Agregar opción vacía al inicio
    const items = await this.buscarPorCategoria('CARDINALIDAD');
    return [{ codigo: '', descripcion: '' }, ...items] as CardinalidadDto[];
  }

  /**
   * Lista tipos de dirección por persona desde BD
   * Equivale a: AdminDireccionesActivity.listarTiposDirecciones()
   */
  async listarTiposDireccionPorPersona(cdPersona: number): Promise<TipoDireccionDto[]> {
    const response = await firstValueFrom(
      this.http.get<TipoDireccionDto[]>(`${this.baseUrl}/tipos-direccion/${cdPersona}`)
    );
    return response || [];
  }

  /**
   * Lista estratos socioeconómicos (1-6)
   * NOTA: No viene de BD, son valores fijos por ley en Colombia
   */
  async listarEstratos(): Promise<NomenclaturaDto[]> {
    return Promise.resolve(this.ESTRATOS_LOCAL);
  }

  /**
   * Lista roles de contacto
   * Equivale a: UtilidadesBuscadores.llenarListBox(widget, CD_ROL, DS_ROL, TABLE_ROLES, ...)
   * Query legacy: SELECT DISTINCT TO_CHAR(CDROL), TO_CHAR(DSROL) FROM TPSC_ROLES
   *               WHERE CDROL IS NOT NULL AND DSROL IS NOT NULL ORDER BY TO_CHAR(DSROL)
   */
  async listarRoles(): Promise<NomenclaturaDto[]> {
    try {
      const response = await firstValueFrom(
        this.http.get<NomenclaturaDto[]>(`${this.baseUrl}/roles`)
      );
      return response || [];
    } catch (error) {
      console.warn('⚠️ Roles no disponibles desde backend');
      return [];
    }
  }

  /**
   * Lista cargos de contacto
   * Equivale a: UtilidadesBuscadores.llenarListBox(widget, CD_CARGO, DS_CARGO, TABLE_CARGOS, ...)
   * Query legacy: SELECT DISTINCT TO_CHAR(CDCARGO), TO_CHAR(DSCARGO) FROM TPSC_CARGOS
   *               WHERE CDCARGO IS NOT NULL AND DSCARGO IS NOT NULL ORDER BY TO_CHAR(DSCARGO)
   */
  async listarCargos(): Promise<NomenclaturaDto[]> {
    try {
      const response = await firstValueFrom(
        this.http.get<NomenclaturaDto[]>(`${this.baseUrl}/cargos`)
      );
      return response || [];
    } catch (error) {
      console.warn('⚠️ Cargos no disponibles desde backend');
      return [];
    }
  }

  // =========================================================================
  // MÉTODOS DE COMPATIBILIDAD LEGACY
  // =========================================================================

  /**
   * Método legacy para compatibilidad
   * @deprecated Usar métodos específicos (listarTiposVia, etc.)
   */
  async buscarItems(request: BusquedaNomenclaturaRequest): Promise<NomenclaturaDto[]> {
    let params = new HttpParams();

    if (request.parametro1) {
      params = params.set('parametro1', request.parametro1);
    }

    if (request.parametro2) {
      params = params.set('parametro2', request.parametro2);
    }

    const response = await firstValueFrom(
      this.http.get<BusquedaNomenclaturaResponse>(`${this.baseUrl}/buscar`, { params })
    );

    return response?.items || [];
  }

  /**
   * Método legacy para migración gradual
   * @deprecated Usar métodos específicos
   */
  async llenarListBoxLegacy(
    campo1: string,
    campo2: string,
    nombreTabla: string,
    parametro1?: string,
    parametro2?: string
  ): Promise<NomenclaturaDto[]> {
    return this.buscarPorCategoria(parametro2 || '');
  }

  // =========================================================================
  // UTILITY METHODS - Helpers para componentes Angular
  // =========================================================================

  /**
   * Convierte lista de nomenclatura a formato PrimeNG dropdown
   */
  toDropdownOptions(items: NomenclaturaDto[], includeEmpty = true) {
    const options = items.map(item => ({
      label: item.descripcion,
      value: item.codigo
    }));

    if (includeEmpty && !items.some(i => i.codigo === '')) {
      options.unshift({ label: '- Seleccione una opción -', value: '' });
    }

    return options;
  }

  /**
   * Convierte tipos de dirección a formato PrimeNG dropdown
   * Usa 'consecutivo' (number) como value para nmtipo_persona_direccion
   *
   * IMPORTANTE: El backend requiere Integer para nmtipo_persona_direccion
   */
  toTipoDireccionDropdownOptions(items: TipoDireccionDto[], includeEmpty = true) {
    const options: { label: string; value: number | null }[] = items.map(item => ({
      label: item.descripcion,
      value: item.consecutivo ?? parseInt(item.codigo, 10) // Usar consecutivo o parsear código
    }));

    if (includeEmpty) {
      options.unshift({ label: '- Seleccione una opción -', value: null });
    }

    return options;
  }

  /**
   * Busca un item por código
   */
  findByCodigo(items: NomenclaturaDto[], codigo: string): NomenclaturaDto | undefined {
    return items.find(item => item.codigo === codigo);
  }

  /**
   * Busca items por descripción parcial (case insensitive)
   */
  searchByDescripcion(items: NomenclaturaDto[], busqueda: string): NomenclaturaDto[] {
    const filtro = busqueda.toLowerCase();
    return items.filter(item =>
      item.descripcion.toLowerCase().includes(filtro)
    );
  }
}

// Re-exportar con nombre alternativo para compatibilidad
export { HttpNomenclaturaRepository as NomenclaturaService };
